import type { JsonSchemaType } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar, CheckCircle2, Folder, LayoutTemplate } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  objectCategoryKeys,
  useCreateObjectCategory,
  useObjectCategory,
  useUpdateObjectCategory,
  useUserGroups,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { DetailCard, DetailPageLayout } from '@/shared/components/layout';
import {
  type FieldError,
  parseMetadataApiErrors,
} from '@/shared/components/metadata/metadata-builder';
import { MetadataBuilderWithPreview } from '@/shared/components/metadata/metadata-builder/metadata-builder-with-preview';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import {
  getObjectCategoryIcon,
  normalizeObjectCategoryIconKey,
  objectCategoryIconOptions,
} from '@/shared/utils/object-category-icons';
import { isGmnAdminGroup } from '@/shared/utils/permissions';

const categoryDetailsSchema = z.object({
  name: z.string().min(1, 'Required'),
  description: z.string().optional(),
  icon: z.string().nullable().optional(),
});

type CategoryDetailsFormValues = z.infer<typeof categoryDetailsSchema>;

const emptyDetailsDefaults: CategoryDetailsFormValues = {
  name: '',
  description: '',
  icon: null,
};

// Default empty schema for new categories
const defaultMetadataSchema: JsonSchemaType = {
  type: 'object',
  groups: [],
  properties: {},
  required: [],
  propertyOrder: [],
};

export function ManageObjectCategory() {
  const { t, tc } = useIntl();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const createCategory = useCreateObjectCategory();
  const updateCategory = useUpdateObjectCategory();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: category, isLoading } = useObjectCategory(id || '', {
    disabled: !id,
  });
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });
  const userGroups = userGroupsData?.docs ?? [];

  const isGmnAdmin = isGmnAdminGroup(currentUser);
  const formDisabled = isLoading || !isGmnAdmin;

  const [isUpdating, setIsUpdating] = useState(false);
  const [metadataSchema, setMetadataSchema] = useState<JsonSchemaType>(defaultMetadataSchema);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const initialDetailsRef = useRef<CategoryDetailsFormValues>(emptyDetailsDefaults);
  const initialSchemaRef = useRef<JsonSchemaType>(defaultMetadataSchema);

  // Form for basic category details
  const detailsForm = useForm<CategoryDetailsFormValues>({
    resolver: zodResolver(categoryDetailsSchema),
    defaultValues: emptyDetailsDefaults,
  });

  // Reset forms when category data loads
  useEffect(() => {
    if (isEditMode && category && !isLoading) {
      const nextDetails: CategoryDetailsFormValues = {
        name: category.name,
        description: category.description || '',
        icon: normalizeObjectCategoryIconKey(category.icon) ?? null,
      };
      const nextSchema = (category.metadataSchema as JsonSchemaType) || defaultMetadataSchema;
      detailsForm.reset(nextDetails);
      setMetadataSchema(nextSchema);
      initialDetailsRef.current = nextDetails;
      initialSchemaRef.current = nextSchema;
    }
  }, [category, isEditMode, isLoading, detailsForm]);

  useEffect(() => {
    if (isEditMode) return;
    initialDetailsRef.current = emptyDetailsDefaults;
    initialSchemaRef.current = defaultMetadataSchema;
  }, [isEditMode]);

  const handleSubmit = async () => {
    if (!isGmnAdmin) return;

    // Validate details form
    const detailsValid = await detailsForm.trigger();
    if (!detailsValid) {
      toast.error(t('validation.pleaseCorrectErrors'));
      return;
    }

    const detailsData = detailsForm.getValues();

    setIsUpdating(true);
    setFieldErrors([]); // Clear previous errors

    try {
      if (isEditMode) {
        await updateCategory.mutateAsync({
          id,
          data: {
            name: detailsData.name,
            description: detailsData.description,
            icon: detailsData.icon,
            metadataSchema,
          },
        });
      } else {
        const created = await createCategory.mutateAsync({
          name: detailsData.name,
          description: detailsData.description,
          icon: detailsData.icon,
          metadataSchema,
        });
        // Navigate to the edit page for the newly created category
        allowNextNavigation();
        navigate(`/gmn/settings/object-categories/${created.doc.id}`, {
          replace: true,
        });
      }

      await queryClient.invalidateQueries({ queryKey: objectCategoryKeys.all });
      toast.success(
        isEditMode
          ? t('objectCategories.categoryUpdatedSuccessfully')
          : t('objectCategories.categoryCreatedSuccessfully')
      );
    } catch (error) {
      // Parse field-specific errors
      const parsedErrors = parseMetadataApiErrors(error, (key) => t(key));
      setFieldErrors(parsedErrors);

      // Handle remaining errors with toasts
      const payloadError = error as {
        response?: {
          errors?: Array<{
            data?: {
              errors?: Array<{
                code: string;
                message: string;
              }>;
            };
            message?: string;
          }>;
        };
      };

      if (payloadError.response?.errors) {
        payloadError.response.errors.forEach((err) => {
          if (err.data?.errors) {
            err.data.errors.forEach((apiError) => {
              // Only show toast for errors that weren't mapped to fields
              if (
                ![
                  'NewRequiredFieldAdditionNotAllowed',
                  'ExistingFieldMadeRequiredWithMissingValues',
                  'FieldTypeChangeNotAllowed',
                ].includes(apiError.code)
              ) {
                toast.error(t('metadata.metadataUpdateFailed'), {
                  description: apiError.message,
                });
              }
            });
          } else if (err.message) {
            toast.error(t('errors.unknownError'), {
              description: err.message,
            });
          }
        });
      } else if (parsedErrors.length === 0) {
        toast.error(t('errors.unknownError'));
      }
    }

    setIsUpdating(false);
  };

  // Clear errors when schema changes
  const handleSchemaChange = (schema: JsonSchemaType) => {
    setMetadataSchema(schema);
    if (fieldErrors.length > 0) {
      setFieldErrors([]);
    }
  };

  const isSchemaDirty = useMemo(
    () => JSON.stringify(metadataSchema) !== JSON.stringify(initialSchemaRef.current),
    [metadataSchema]
  );

  const discardChanges = () => {
    detailsForm.reset(initialDetailsRef.current);
    setMetadataSchema(initialSchemaRef.current);
    setFieldErrors([]);
  };

  const { dialog: unsavedDialog, allowNextNavigation } = useUnsavedChangesGuard({
    when: detailsForm.formState.isDirty || isSchemaDirty,
    onDiscard: discardChanges,
  });

  const getPageTitle = () =>
    isEditMode ? t('objectCategories.editCategory') : t('objectCategories.newCategory');

  const watchedIcon = detailsForm.watch('icon');
  const HeaderIcon = getObjectCategoryIcon(watchedIcon ?? category?.icon ?? null);

  const fieldCount =
    isEditMode && category
      ? Object.keys(
          ((category.metadataSchema as { properties?: Record<string, unknown> })?.properties ??
            {}) as Record<string, unknown>
        ).length
      : 0;

  const metaItems =
    isEditMode && category
      ? [
          {
            icon: LayoutTemplate,
            label:
              fieldCount === 0
                ? t('objectCategories.metadataFieldCount.none')
                : tc('objectCategories.metadataFieldCount', fieldCount),
          },
          ...(category.createdAt
            ? [
                {
                  icon: Calendar,
                  label: `${t('common.since')} ${DateTime.fromISO(category.createdAt).toFormat('dd.MM.yyyy')}`,
                },
              ]
            : []),
        ]
      : undefined;

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref: '/gmn/settings/object-categories',
        backLabel: t('objectCategories.backToOverview'),
        title: isEditMode && category ? category.name : getPageTitle(),
        avatarContent: <HeaderIcon size={32} />,
        isCreateMode: !isEditMode,
        metaItems,
        subtitle: !isEditMode ? t('objectCategories.createNewCategoryHint') : undefined,
        secondaryAction: {
          label: t('common.discard'),
          onClick: () => navigate('/gmn/settings/object-categories'),
        },
        primaryAction: {
          label: isEditMode
            ? isUpdating
              ? t('common.updating')
              : t('common.update')
            : isUpdating
              ? t('common.creating')
              : t('common.create'),
          onClick: () => void handleSubmit(),
          disabled: !isGmnAdmin || isUpdating,
          loading: isUpdating,
        },
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Form {...detailsForm}>
          <DetailCard
            icon={Folder}
            title={t('objectCategories.categoryDetails')}
            className="lg:col-span-2"
          >
            <p className="text-sm text-muted-foreground mb-6">
              {t('objectCategories.categoryDetailsDescription')}
            </p>
            <div className="space-y-4">
              <FormField
                control={detailsForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('common.name')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={formDisabled}
                        placeholder={t('objectCategories.namePlaceholder')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={detailsForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.description')}</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        disabled={formDisabled}
                        placeholder={t('objectCategories.descriptionPlaceholder')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DetailCard>

          <DetailCard
            icon={CheckCircle2}
            title={t('objectCategories.icon')}
            className="lg:col-span-1"
            noPadding
            headerAction={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8"
                disabled={formDisabled || !detailsForm.getValues('icon')}
                onClick={() => detailsForm.setValue('icon', null, { shouldDirty: true })}
              >
                {t('common.remove')}
              </Button>
            }
          >
            <div className="p-4 overflow-y-auto max-h-[420px]">
              <FormField
                control={detailsForm.control}
                name="icon"
                render={({ field }) => {
                  const selected = field.value ?? null;

                  return (
                    <FormItem>
                      <div className="grid grid-cols-5 gap-2">
                        {objectCategoryIconOptions.map(({ key, Icon }) => {
                          const isSelected = selected === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => field.onChange(key)}
                              title={key}
                              disabled={formDisabled}
                              data-testid={`object-category-icon-option-${key}`}
                              aria-pressed={isSelected}
                              className={`aspect-square flex flex-col items-center justify-center gap-1 rounded-lg border transition-all ${
                                isSelected
                                  ? 'bg-primary/10 border-primary/40 text-primary shadow-sm dark:bg-primary/20'
                                  : 'bg-background border-border text-muted-foreground hover:bg-muted hover:text-foreground hover:border-primary/30'
                              }`}
                            >
                              <Icon className="size-5" />
                            </button>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          </DetailCard>
        </Form>

        <DetailCard
          icon={LayoutTemplate}
          title={t('objectCategories.metadataSchema')}
          noPadding
          className="min-h-[500px] lg:col-span-3"
        >
          <div className="min-h-[500px]">
            <MetadataBuilderWithPreview
              value={metadataSchema}
              onChange={handleSchemaChange}
              disabled={!isGmnAdmin || isLoading}
              fieldErrors={fieldErrors}
              userGroups={userGroups}
              allowPhotos
              header={false}
              storageKey="gmn.metadataBuilder.objectCategory.formPreview.open"
            />
          </div>
        </DetailCard>
      </div>

      {unsavedDialog}
    </DetailPageLayout>
  );
}
